/**
 * Demo Mode Team Builder Storage
 *
 * Persists team builder state to localStorage when in demo mode,
 * so users can play with the Team Builder without writing to the database.
 * State survives page refreshes within the same browser session.
 *
 * Requirements:
 * - Save/load team formations (teams array) keyed by tryout ID
 * - Serialize only IDs and positions (not full athlete objects) to stay within
 *   localStorage size limits (~322 KB for 60 athletes / 5 teams)
 * - Provide clear/cleanup when exiting demo mode
 */

import { TeamFormation, TeamBuilderAthlete, StuntGroupAssignment, StuntGroupSlotName } from '@/utils/types/teamBuilder';
import { createLogger } from '@/utils/logger/devLogger';

const logger = createLogger('DemoTeamBuilderStorage');

const STORAGE_PREFIX = 'demo_team_builder_';

// ============================================================================
// SERIALIZATION TYPES
// ============================================================================

/** Lightweight representation of a team for localStorage */
interface SerializedTeam {
  id: string;
  name: string;
  divisionLevel: string;
  ageCategory: string;
  usasf_division_id?: string;
  status?: 'draft' | 'finalized';
  /** Roster athlete IDs */
  athleteIds: string[];
  /** Position map: athleteId → assigned position */
  positionMap: Record<string, string>;
  /** Assignment type map: athleteId → 'primary' | 'crossover' */
  assignmentTypeMap: Record<string, string>;
  /** Alternate athlete IDs */
  alternateIds: string[];
  /** Stunt group slot assignments (IDs only) */
  stuntGroups?: SerializedStuntGroup[];
  /** Athlete notes */
  athleteNotes?: Record<string, string>;
  /** Coach assignments */
  headCoach?: string | null;
  assistantCoaches?: string[];
  /** Grade and bid data (preserved as-is since they're plain objects) */
  grade?: TeamFormation['grade'];
  bidProbability?: number;
}

interface SerializedStuntGroup {
  id: string;
  label: string;
  slotIds: Record<string, string | null>;
  extraIds: string[];
}

interface DemoTeamBuilderState {
  version: 1;
  savedAt: string;
  tryoutId: string;
  teams: SerializedTeam[];
}

// ============================================================================
// SERIALIZE / DESERIALIZE
// ============================================================================

function serializeTeam(team: TeamFormation): SerializedTeam {
  const positionMap: Record<string, string> = {};
  const assignmentTypeMap: Record<string, string> = {};

  team.athletes.forEach(a => {
    if (a.assignedPosition) positionMap[a.id] = a.assignedPosition;
    if (a.assignmentType) assignmentTypeMap[a.id] = a.assignmentType;
  });

  let stuntGroups: SerializedStuntGroup[] | undefined;
  if (team.stuntGroups && team.stuntGroups.length > 0) {
    stuntGroups = team.stuntGroups.map(sg => {
      const slotIds: Record<string, string | null> = {};
      const slotNames: StuntGroupSlotName[] = ['flyer', 'mainBase', 'sideBase', 'backSpot', 'frontSpot'];
      slotNames.forEach(name => {
        slotIds[name] = sg.slots[name]?.id || null;
      });
      return {
        id: sg.id,
        label: sg.label,
        slotIds,
        extraIds: sg.extras.map(a => a.id),
      };
    });
  }

  return {
    id: team.id,
    name: team.name,
    divisionLevel: team.divisionLevel,
    ageCategory: team.ageCategory,
    usasf_division_id: team.usasf_division_id,
    status: team.status,
    athleteIds: team.athletes.map(a => a.id),
    positionMap,
    assignmentTypeMap,
    alternateIds: (team.alternates || []).map(a => a.id),
    stuntGroups,
    athleteNotes: team.athleteNotes,
    headCoach: team.headCoach,
    assistantCoaches: team.assistantCoaches,
    grade: team.grade,
    bidProbability: team.bidProbability,
  };
}

/**
 * Hydrate serialized teams back into full TeamFormation objects
 * using the converted athletes array as a lookup source.
 */
export function hydrateTeams(
  serialized: SerializedTeam[],
  allAthletes: TeamBuilderAthlete[]
): { teams: TeamFormation[]; assignedIds: Set<string> } {
  const athleteMap = new Map(allAthletes.map(a => [a.id, a]));
  const assignedIds = new Set<string>();

  const teams = serialized.map(st => {
    // Reconstruct roster athletes
    const athletes = st.athleteIds
      .map(id => athleteMap.get(id))
      .filter((a): a is TeamBuilderAthlete => !!a)
      .map(a => ({
        ...a,
        assignedPosition: (st.positionMap[a.id] || a.assignedPosition) as TeamBuilderAthlete['assignedPosition'],
        assignmentType: st.assignmentTypeMap[a.id] as 'primary' | 'crossover' | undefined,
      }));

    athletes.forEach(a => assignedIds.add(a.id));

    // Reconstruct alternates
    const alternates = st.alternateIds
      .map(id => athleteMap.get(id))
      .filter((a): a is TeamBuilderAthlete => !!a);

    alternates.forEach(a => assignedIds.add(a.id));

    // Reconstruct stunt groups
    let stuntGroups: StuntGroupAssignment[] | undefined;
    if (st.stuntGroups && st.stuntGroups.length > 0) {
      stuntGroups = st.stuntGroups.map(sg => ({
        id: sg.id,
        label: sg.label,
        slots: {
          flyer: sg.slotIds.flyer ? athleteMap.get(sg.slotIds.flyer) || null : null,
          mainBase: sg.slotIds.mainBase ? athleteMap.get(sg.slotIds.mainBase) || null : null,
          sideBase: sg.slotIds.sideBase ? athleteMap.get(sg.slotIds.sideBase) || null : null,
          backSpot: sg.slotIds.backSpot ? athleteMap.get(sg.slotIds.backSpot) || null : null,
          frontSpot: sg.slotIds.frontSpot ? athleteMap.get(sg.slotIds.frontSpot) || null : null,
        },
        extras: sg.extraIds
          .map(id => athleteMap.get(id))
          .filter((a): a is TeamBuilderAthlete => !!a),
      }));
    }

    // Reconstruct position distribution
    const positions = {
      flyers: athletes.filter(a => a.assignedPosition === 'flyer'),
      bases: athletes.filter(a => a.assignedPosition === 'base'),
      backSpots: athletes.filter(a => a.assignedPosition === 'back_spot'),
      frontSpots: athletes.filter(a => a.assignedPosition === 'front_spot'),
      tumblers: athletes.filter(a => a.assignedPosition === 'tumbler'),
      dancers: athletes.filter(a => a.assignedPosition === 'dancer'),
    };

    return {
      id: st.id,
      name: st.name,
      divisionLevel: st.divisionLevel,
      ageCategory: st.ageCategory || 'Open',
      usasf_division_id: st.usasf_division_id,
      status: st.status,
      athletes,
      alternates,
      stuntGroups,
      athleteNotes: st.athleteNotes,
      headCoach: st.headCoach,
      assistantCoaches: st.assistantCoaches,
      grade: st.grade || {
        overallScore: 0,
        letterGrade: 'N/A' as const,
        skillBreakdown: {
          tumbling: { score: 0, grade: 'N/A' as const },
          stunting: { score: 0, grade: 'N/A' as const },
          dance: { score: 0, grade: 'N/A' as const },
          jumps: { score: 0, grade: 'N/A' as const },
        },
        positionQuality: 0,
        balanceScore: 0,
        continuityScore: 0,
      },
      bidProbability: st.bidProbability || 0,
      positions,
      strengths: [],
      weaknesses: [],
      validation: { isValid: false, errors: [], warnings: [] },
    } as TeamFormation;
  });

  return { teams, assignedIds };
}

// ============================================================================
// PUBLIC API
// ============================================================================

/**
 * Save demo team builder state to localStorage.
 */
export function saveDemoTeamBuilderState(tryoutId: string, teams: TeamFormation[]): void {
  try {
    const state: DemoTeamBuilderState = {
      version: 1,
      savedAt: new Date().toISOString(),
      tryoutId,
      teams: teams.map(serializeTeam),
    };

    const key = `${STORAGE_PREFIX}${tryoutId}`;
    const json = JSON.stringify(state);
    localStorage.setItem(key, json);

    logger.info('Demo team builder state saved to localStorage', {
      tryoutId,
      teamCount: teams.length,
      sizeBytes: json.length,
    });
  } catch (error) {
    // localStorage might be full or disabled — fail silently
    logger.warn('Failed to save demo team builder state', error);
  }
}

/**
 * Load demo team builder state from localStorage.
 * Returns null if no saved state exists.
 */
export function loadDemoTeamBuilderState(tryoutId: string): DemoTeamBuilderState | null {
  try {
    const key = `${STORAGE_PREFIX}${tryoutId}`;
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const state: DemoTeamBuilderState = JSON.parse(raw);
    if (state.version !== 1 || state.tryoutId !== tryoutId) return null;

    logger.info('Loaded demo team builder state from localStorage', {
      tryoutId,
      teamCount: state.teams.length,
      savedAt: state.savedAt,
    });

    return state;
  } catch (error) {
    logger.warn('Failed to load demo team builder state', error);
    return null;
  }
}

/**
 * Clear demo team builder state for a specific tryout.
 */
export function clearDemoTeamBuilderState(tryoutId: string): void {
  try {
    const key = `${STORAGE_PREFIX}${tryoutId}`;
    localStorage.removeItem(key);
    logger.info('Cleared demo team builder state', { tryoutId });
  } catch {
    // Ignore errors during cleanup
  }
}

/**
 * Clear ALL demo team builder states (called when exiting demo mode).
 */
export function clearAllDemoTeamBuilderStates(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    logger.info('Cleared all demo team builder states', { count: keysToRemove.length });
  } catch {
    // Ignore errors during cleanup
  }
}
